import type { PropsWithChildren } from "react";
import { useCallback, useState } from "react";

import { useModalDialog } from "@skilitics/designer-ui-components";

import { useFeatureSwitch } from "../application/index.ts";
import { BrowseTemplates, browseTemplatesDialogId, browseTemplatesModalDialogProps } from "../nodes/templates/index.ts";
import { useCreateNode, useMutateNodeDesigns } from "../state/index.ts";
import type { DesignerNode, FindDefinitions } from "../types";
import { ChildNodeSlottingContext } from "./ChildNodeSlottingContext.ts";
import { DefinitionBrowser } from "./DefinitionBrowser.tsx";
import { LibraryContext } from "./LibraryContext.ts";
import type { SelectorOptions } from "./shared.ts";

type Props = {
  findDefinitions: FindDefinitions;
};

export const ChildNodeSlottingProvider = ({ findDefinitions, children }: PropsWithChildren<Props>) => {
  const { openModalDialog, closeModalDialog } = useModalDialog();
  const { add, replace } = useMutateNodeDesigns();
  const createNode = useCreateNode();

  const isSuperUser = useFeatureSwitch("super-user");

  // this whole component needs a tidy up as very confusing but when this is set you are in definition picker mode
  const [selectorOptions, setSelectorOptions] = useState<SelectorOptions>();

  const handleClose = useCallback(() => {
    setSelectorOptions(undefined);
    closeModalDialog(browseTemplatesDialogId);
  }, [closeModalDialog]);

  const handleSelected = useCallback(
    (newNode: DesignerNode) => {
      selectorOptions?.onCompleted?.(newNode);
      handleClose();
    },
    [handleClose, selectorOptions],
  );

  const openSelector = useCallback(
    (args: SelectorOptions) => {
      const options = {
        ...args,
        criteria: { ...args.criteria, scope: "system" as const },
      };

      const handleCreate: Parameters<typeof BrowseTemplates>[0]["onCreate"] = (createType) => {
        const nodeCreate = (typeId: string) => {
          const newNode = createNode(typeId, {});

          const { target } = options;

          if ("slotId" in target) {
            const { parentId, name } = target.slotId;
            add(newNode, { parentId, name });
          } else if ("slotResidentNodeId" in target) {
            replace(target.slotResidentNodeId, newNode);
          } else {
            throw new Error(`target type ${JSON.stringify(target)} not supported here.`);
          }
          options?.onCompleted?.(newNode);
        };

        switch (createType) {
          case "activity": {
            // note: developers can choose an activity, others are simply
            //       provided with a composable activity to customize
            if (isSuperUser) {
              setSelectorOptions(options);
            } else {
              nodeCreate("composableActivity"); // TODO: move this into app inserted. core shouldn't know about specific nodes
              handleClose();
            }
            break;
          }

          case "grouping": {
            nodeCreate("navGroup"); // TODO: move this into app inserted. core shouldn't know about specific nodes
            handleClose();
            break;
          }
        }
      };

      openModalDialog(
        {
          ...browseTemplatesModalDialogProps,
          children: (
            <BrowseTemplates
              {...options}
              onSelected={(node) => {
                options?.onCompleted?.(node);
                handleClose();
              }}
              onClose={handleClose}
              onCreate={handleCreate}
            />
          ),
        },
        browseTemplatesDialogId,
      );
    },
    [add, createNode, handleClose, isSuperUser, openModalDialog, replace],
  );

  return (
    <ChildNodeSlottingContext.Provider value={{ openSelector }}>
      <LibraryContext.Provider value={{ findDefinitions }}>
        {children}
        {selectorOptions && (
          <DefinitionBrowser
            selectorOptions={selectorOptions}
            onSelected={handleSelected}
            onClose={() => setSelectorOptions(undefined)}
          />
        )}
      </LibraryContext.Provider>
    </ChildNodeSlottingContext.Provider>
  );
};
